import { Fragment, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ChevronRight } from "lucide-react";
import { api, qs } from "@/lib/api";
import { Card, SectionTitle } from "@/components/Kpi";
import { useTitoliPagina } from "@/components/TitoliPagina";
import { FilterBar, inputCls, useFiltersMeta } from "@/components/Filters";
import { eurShort, num, troncaNome } from "@/lib/format";
import DealerForm from "@/components/DealerForm";

const CLASSIFICHE = [
  { key: "pratiche_caricate", label: "Pratiche caricate", kind: "num", colore: "red" },
  { key: "importo_caricato", label: "Importo caricato", kind: "eur", colore: "red" },
  { key: "pratiche_liquidate", label: "Pratiche liquidate", kind: "num", colore: "green" },
  { key: "importo_liquidato", label: "Importo liquidato", kind: "eur", colore: "green" },
];

// red/green sono le chiavi storiche di caricato/liquidato: indaco e petrolio
const STILI = {
  red: { card: "!border-2 bd-caricato", head: "txt-caricato", val: "txt-caricato",
    bar: "bg-caricato" },
  green: { card: "!border-2 bd-liquidato", head: "txt-liquidato", val: "txt-liquidato",
    bar: "bg-liquidato" },
};

function Tabella({ conf, rows, baseRows, onCrea, T }) {
  const s = STILI[conf.colore];
  const [aperti, setAperti] = useState({});
  const toggle = (c) => setAperti((a) => ({ ...a, [c]: !a[c] }));
  const ordinate = [...rows].filter((r) => r[conf.key] > 0).sort((a, b) => b[conf.key] - a[conf.key]);
  const fmt = (v) => (conf.kind === "eur" ? eurShort(v) : num(v, 0));
  // il totale di riferimento rispetta solo i filtri anno/commerciale/settore/dealer (non ricerca e incomplete)
  const totale = (baseRows || rows).reduce((t, r) => t + (r[conf.key] > 0 ? r[conf.key] : 0), 0);
  const quota = (v) => (totale ? (v / totale) * 100 : 0);
  return (
    <Card className={`p-0 overflow-hidden ${s.card}`} data-testid={`classifica-${conf.key}`}>
      <div className="px-6 py-4 flex items-baseline justify-between flex-wrap gap-2">
        <h3 className={`uppercase text-xs tracking-[0.18em] font-semibold ${s.head}`}>
          <T chiave={`classifica-${conf.key}`} originale={`Classifica · ${conf.label}`} />
        </h3>
        <span className="font-mono text-sm text-slate-500">
          {num(ordinate.length)} convenzionati · totale {fmt(totale)}
        </span>
      </div>
      <div className="max-h-[460px] overflow-y-auto">
        <table className="w-full text-sm">
          <thead className="sticky top-0 bg-slate-50 text-slate-500 uppercase text-[10px] tracking-wider">
            <tr>
              <th className="text-left px-4 py-2 w-14">#</th>
              <th className="text-left px-4 py-2">Convenzionato</th>
              <th className="text-left px-4 py-2">Commerciale</th>
              <th className="text-left px-4 py-2">Settore</th>
              <th className="text-right px-4 py-2">{conf.label}</th>
              <th className="text-right px-4 py-2">% firma dig.</th>
              <th className="text-right px-4 py-2">% privacy</th>
              <th className="px-4 py-2 w-40">Peso · % sul totale</th>
            </tr>
          </thead>
          <tbody>
            {ordinate.map((r, i) => (
              <Fragment key={r.codice_dealer}>
              <tr
                className={`border-t border-slate-100 hover:bg-sky-50/60 transition-colors duration-150
                  ${i % 2 ? "bg-slate-100/80" : "bg-white"}`}>
                <td className="px-4 py-2 font-mono text-xs text-slate-400">{i + 1}</td>
                <td className="px-4 py-2 whitespace-nowrap">
                  {r.punti_vendita?.length > 1 && (
                    <button type="button" onClick={() => toggle(r.codice_dealer)} data-testid={`pv-toggle-${conf.key}-${r.codice_dealer}`}
                      title={`${r.punti_vendita.length} punti vendita`}
                      className="mr-1.5 inline-flex items-center justify-center w-5 h-5 rounded border border-slate-300 text-slate-500
                        hover:border-sky-500 hover:text-sky-700 transition-colors duration-200">
                      <ChevronRight size={12} className={`transition-transform duration-200 ${aperti[r.codice_dealer] ? "rotate-90" : ""}`} />
                    </button>
                  )}
                  <Link to={`/convenzionati/${r.codice_dealer}`} className="hover:underline font-bold"
                    title={r.ragione_sociale}>
                    {troncaNome(r.ragione_sociale)}
                  </Link>
                  {(!r.in_anagrafica || r.dati_incompleti) && (
                    <button type="button" onClick={() => onCrea(r)}
                      data-testid={`crea-anagrafica-${r.codice_dealer}`}
                      title={r.in_anagrafica ? "Anagrafica incompleta: aggiungi partita IVA e provincia" : "Crea l'anagrafica di questo convenzionato"}
                      className="ml-2 text-[10px] uppercase tracking-wider text-white bg-indigo-700 rounded px-1.5 py-0.5 hover:bg-indigo-800 transition-colors duration-200">
                      {r.in_anagrafica ? "anagrafica incompleta" : "no anagrafica"}
                    </button>
                  )}
                </td>
                <td className="px-4 py-2 text-slate-500">{r.commerciale_nome}</td>
                <td className="px-4 py-2 text-slate-500">{r.settore}</td>
                <td className={`px-4 py-2 text-right font-mono font-semibold whitespace-nowrap ${s.val}`}>{fmt(r[conf.key])}</td>
                <td className="px-4 py-2 text-right font-mono text-slate-500 whitespace-nowrap">
                  {r.perc_firma_digitale === null || r.perc_firma_digitale === undefined ? "—" : `${num(r.perc_firma_digitale, 2)}%`}
                </td>
                <td className="px-4 py-2 text-right font-mono text-slate-500 whitespace-nowrap">
                  {r.perc_privacy === null || r.perc_privacy === undefined ? "—" : `${num(r.perc_privacy, 2)}%`}
                </td>
                <td className="px-4 py-2">
                  <div className="flex items-center gap-2">
                    <div className="h-1.5 rounded bg-slate-100 flex-1">
                      <div className={`h-1.5 rounded ${s.bar}`} style={{ width: `${Math.min(quota(r[conf.key]), 100)}%` }} />
                    </div>
                    <span className={`font-mono text-[11px] tabular-nums w-14 text-right ${s.val}`}
                      data-testid={`quota-${conf.key}-${r.codice_dealer}`}>
                      {num(quota(r[conf.key]), 1)}%
                    </span>
                  </div>
                </td>
              </tr>
              {aperti[r.codice_dealer] && r.punti_vendita.map((pv) => (
                <tr key={`${r.codice_dealer}-${pv.codice_pv}`} className="border-t border-slate-100 bg-sky-50/40 text-xs"
                  data-testid={`pv-riga-${conf.key}-${pv.codice_pv}`}>
                  <td />
                  <td className="px-4 py-1.5 pl-12 text-slate-600 whitespace-nowrap">
                    <span className="text-slate-400 mr-1">↳</span>{pv.ragione_sociale_pv}
                    <span className="ml-2 font-mono text-slate-400">{pv.codice_pv}</span>
                  </td>
                  <td colSpan={2} className="px-4 py-1.5 text-slate-400">punto vendita</td>
                  <td className={`px-4 py-1.5 text-right font-mono ${s.val}`}>{fmt(pv[conf.key])}</td>
                  <td colSpan={2} />
                  <td className="px-4 py-1.5 font-mono text-[11px] text-slate-500 text-right">
                    {r[conf.key] ? `${num((pv[conf.key] || 0) / r[conf.key] * 100, 1)}% del convenzionato` : ""}
                  </td>
                </tr>
              ))}
              </Fragment>
            ))}
            {!ordinate.length && (
              <tr><td colSpan={8} className="px-4 py-6 text-slate-400">Nessun dato per i filtri selezionati.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </Card>
  );
}

export default function ClassificheDealer() {
  const meta = useFiltersMeta();
  const [filters, setFilters] = useState({ anno: 2026 });
  const [search, setSearch] = useState("");
  const [data, setData] = useState(null);
  const [preset, setPreset] = useState(null);
  const [dealer, setDealer] = useState(null);
  const [soloIncomplete, setSoloIncomplete] = useState(false);
  const [reload, setReload] = useState(0);
  const [inSerie, setInSerie] = useState(false);
  const { T, Barra } = useTitoliPagina("classifiche");
  const [ultimo, setUltimo] = useState(null);
  const [serie, setSerie] = useState(false);
  const [dealers, setDealers] = useState([]);

  useEffect(() => {
    api.get(`/dealers-classifiche?${qs({ anno: filters.anno, commerciale: filters.commerciale, settore: filters.settore, dealer: filters.dealer })}`)
      .then((r) => setData(r.data)).catch(() => setData(null));
  }, [filters, reload]);

  useEffect(() => {
    api.get(`/dashboard/dealers-periodo?${qs({ anno: filters.anno, commerciale: filters.commerciale })}`)
      .then((r) => setDealers(r.data.dealers || [])).catch(() => setDealers([]));
  }, [filters.anno, filters.commerciale]);

  const rows = useMemo(() => {
    const q = search.trim().toLowerCase();
    let all = data?.rows || [];
    if (soloIncomplete) all = all.filter((r) => r.dati_incompleti || !r.in_anagrafica);
    return q ? all.filter((r) => (r.ragione_sociale || "").toLowerCase().includes(q)) : all;
  }, [data, search, soloIncomplete]);

  const daCompletare = (rows || []).filter((r) => r.dati_incompleti || !r.in_anagrafica)
    .sort((a, b) => b.pratiche_caricate - a.pratiche_caricate);
  const incomplete = (data?.rows || []).filter((r) => r.dati_incompleti || !r.in_anagrafica).length;

  const apri = (r) => {
    if (r.in_anagrafica) {
      api.get(`/dealers/${r.codice_dealer}`).then((res) => { setPreset(null); setDealer(res.data.dealer); });
    } else {
      setDealer(null);
      setPreset({
        codice_dealer: r.codice_dealer, ragione_sociale: r.ragione_sociale,
        commerciale_slug: r.commerciale_slug || "", settore: r.settore || "",
        data_attivazione: new Date().toISOString().slice(0, 10),
      });
    }
  };

  // "salva e vai": dopo il salvataggio apre la prossima anagrafica incompleta
  useEffect(() => {
    if (!inSerie || !data) return;
    const next = (data.rows || [])
      .filter((r) => (r.dati_incompleti || !r.in_anagrafica) && r.codice_dealer !== ultimo)
      .sort((a, b) => b.pratiche_caricate - a.pratiche_caricate)[0];
    setInSerie(false);
    if (next) apri(next); else { setPreset(null); setDealer(null); }
  }, [data, inSerie]);

  return (
    <div data-testid="classifiche-page">
      <Barra />
      <SectionTitle sub={data ? `${num(rows.length)} convenzionati con produzione nel ${data.anno}` : "Caricamento…"}>
        <T chiave="titolo" originale="Classifiche convenzionati" />
      </SectionTitle>

      <div className="flex flex-wrap items-center gap-3 mb-6">
        <FilterBar meta={meta} value={filters} onChange={setFilters} dealers={dealers}
          fields={["anno", "commerciale", "settore", "dealer"]} />
        <input className={`${inputCls} min-w-[260px] mb-6`} placeholder="Cerca ragione sociale…"
          value={search} onChange={(e) => setSearch(e.target.value)} data-testid="classifiche-search" />
        <button type="button" onClick={() => setSoloIncomplete((v) => !v)} data-testid="filter-incomplete"
          className={`mb-6 rounded-md px-4 py-2 text-sm border-2 transition-colors duration-200 ${soloIncomplete
            ? "bg-indigo-700 border-indigo-700 text-white hover:bg-indigo-800"
            : "bg-white border-indigo-300 text-indigo-700 hover:border-indigo-500"}`}>
          Solo anagrafiche incomplete{incomplete ? ` (${num(incomplete)})` : ""}
        </button>
        <button type="button" data-testid="btn-completa-in-serie"
          onClick={() => { setSerie(true); setSoloIncomplete(true); if (daCompletare[0]) apri(daCompletare[0]); }}
          disabled={!incomplete}
          className="mb-6 rounded-md px-4 py-2 text-sm bg-slate-900 text-white hover:bg-slate-700 disabled:opacity-40 transition-colors duration-200">
          Completa in serie
        </button>
        <label className="mb-6 flex items-center gap-2 text-sm text-slate-500">
          <input type="checkbox" checked={serie} onChange={(e) => setSerie(e.target.checked)} data-testid="toggle-serie" />
          Salva e vai alla prossima
        </label>
      </div>

      {(preset || dealer) && (
        <div className="mb-6" data-testid="crea-anagrafica-form">
          <DealerForm meta={meta} preset={preset} dealer={dealer || undefined}
            onCancel={() => { setPreset(null); setDealer(null); }}
            onSaved={() => {
              setUltimo(dealer?.codice_dealer || preset?.codice_dealer || null);
              setPreset(null); setDealer(null);
              if (serie) setInSerie(true);
              setReload((v) => v + 1);
            }} />
          {serie && (
            <p className="text-xs text-slate-500 mt-2" data-testid="serie-nota">
              Modalità in serie attiva: al salvataggio si apre la prossima anagrafica incompleta
              ({num(Math.max(daCompletare.length - 1, 0))} rimanenti).
            </p>
          )}
        </div>
      )}

      <div className="space-y-6">
        {CLASSIFICHE.map((c) => (
          <Tabella key={c.key} conf={c} rows={rows} baseRows={data?.rows || []} onCrea={apri} T={T} />
        ))}
      </div>
    </div>
  );
}
